/**
 * Routing constants and configuration
 * Shared constants for route validation and redirect handling
 * 
 * SECURITY: Whitelist approach prevents open redirect attacks
 */

/**
 * Whitelist of allowed redirect paths
 * Used to validate return URLs and prevent open redirect attacks
 * 
 * SECURITY: Only paths in this list are allowed for post-authentication redirects
 */
export const ALLOWED_REDIRECT_PATHS = [
  '/',
  '/home',
  '/products',
  '/product',
  '/product/',
  '/categories',
  '/category',
  '/category/',
  '/cart',
  '/checkout',
  '/orders',
  '/order',
  '/order/',
  '/profile',
  '/settings',
  '/email-verification',
  '/store-page',
  '/store',
  '/store/',
  '/seller',
  '/seller/',
  '/stores',
  '/wishlist',
  '/recently-viewed',
  '/popular-products',
  '/featured-products',
  '/flash-sale',
  '/search',
  '/contact',
  '/about',
  '/help',
  '/terms',
  '/privacy',
  '/shipping',
  '/returns',
  '/baakas-university',
  '/loyalty-coins',
  '/loyalty-points',
  '/vouchers',
  '/following',
  '/messaging',
  // AFFILIATE (disabled for now): '/affiliate',
] as const;

/**
 * Check if a path is in the allowed redirect paths whitelist
 * 
 * @param path - The path to check
 * @returns true if path is in the whitelist
 */
export function isAllowedRedirectPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  
  return ALLOWED_REDIRECT_PATHS.some(allowedPath => {
    if (allowedPath.endsWith('/')) {
      return path.startsWith(allowedPath);
    }
    return path === allowedPath || path.startsWith(allowedPath + '/');
  });
}

const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/auth/login', '/auth/register', '/email-verification'];

/**
 * Validates and sanitizes a return URL for post-auth redirect.
 * Returns the path to use (without query/hash) or null if invalid.
 * Use for both storing (authReturnUrl) and reading before navigate.
 */
export function getValidatedReturnUrl(url: string | null): string | null {
  if (!url || typeof url !== 'string' || !url.startsWith('/')) return null;
  const sanitized = url
    .replace(/[<>"'&]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '')
    .split('?')[0]
    .split('#')[0]
    .trim();
  if (!sanitized || sanitized.length > 500) return null;
  if (sanitized.includes('//') || sanitized.includes('@') || sanitized.includes('..') ||
      sanitized.includes('%2e%2e') || sanitized.includes('%2f') || sanitized.includes('%3a') || sanitized.includes('%3f'))
    return null;
  if (AUTH_PATHS.some(p => sanitized === p || sanitized.startsWith(p + '/'))) return null;
  return isAllowedRedirectPath(sanitized) ? sanitized : null;
}

